import React, { useState, useEffect, useRef } from 'react';
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../contexts/ToastContext.jsx';
import { useTheme } from '../contexts/ThemeContext.jsx';
import Step3AIConfig from '../components/Step3AIConfig.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import WizardProgressIndicator from '../components/WizardProgressIndicator.jsx';
import WizardBreadcrumbs from '../components/WizardBreadcrumbs.jsx';
import WizardNavigationConfirmDialog from '../components/WizardNavigationConfirmDialog.jsx';
import { ApiClient } from '../utils/apiClient.js';
import { useWizardNavigation } from '../hooks/useWizardNavigation.js';
import { useWizardSessionManager } from '../utils/wizardSessionManager.js';

/**
 * Parrot Wizard Step 3 Page
 * Handles OpenAI chat interface for system prompt refinement
 */
export default function ParrotWizardStep3() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // For editing mode
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  const apiClient = useRef(new ApiClient(getToken)).current;
  
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [step1Data, setStep1Data] = useState({});
  const [step2Data, setStep2Data] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [voices, setVoices] = useState([]);
  const [templates, setTemplates] = useState([]);

  // TASK 12: Initialize session manager for KB configuration handling
  const sessionManager = useWizardSessionManager(
    step1Data.session_id,
    id, // parrotId for editing mode
    getToken
  );

  // Wizard navigation with unsaved changes protection
  const wizardNavigation = useWizardNavigation({
    sessionId: step1Data.session_id,
    parrotId: id,
    currentStep: 3,
    onSaveChanges: async () => {
      // Save current form data before navigation
      const combinedData = {
        ...step1Data,
        ...step2Data,
        ...formData
      };
      await wizardNavigation.saveCurrentStep(combinedData, { completed: false });
    },
    autoSave: false
  });

  /**
   * Fetch voices and templates data
   */
  const fetchReferenceData = async () => {
    try {
      // Fetch voices and templates in parallel
      const [voicesData, templatesData] = await Promise.all([
        apiClient.get('/api/voices'),
        apiClient.get('/api/quick-action-templates/active')
      ]);
      
      setVoices(voicesData || []);
      setTemplates(templatesData.templates || []);
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };

  // Check if we're in editing mode and load data
  useEffect(() => {
    if (id) {
      setIsEditing(true);
    }

    // Fetch reference data first
    fetchReferenceData();

    // Get previous step data from navigation state
    if (location.state?.step1Data && location.state?.step2Data) {
      setStep1Data(location.state.step1Data);
      setStep2Data(location.state.step2Data);
      
      // Combine all data for Step 3
      const combinedData = {
        ...location.state.step1Data,
        ...location.state.step2Data
      };
      setFormData(combinedData);
      
    } else {
      // If no previous step data, redirect back to step 1
      const step1Route = isEditing ? `/parrots/${id}/edit/step1` : '/parrots/new/step1';
      navigate(step1Route);
    }
  }, [id, location.state]);

  // Update selected template when templates are loaded
  useEffect(() => {
    if (templates.length > 0 && step1Data.selected_template_id) {
      const template = templates.find(t => t.id === step1Data.selected_template_id);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [templates, step1Data.selected_template_id]);

  /**
   * Handle completion of parrot configuration
   */
  const handleComplete = async (finalData) => {
    try {
      setIsLoading(true);
      setErrors({});

      // Combine all step data with final AI configuration
      // TASK 12: Enhanced finalization with knowledge base handling
      const completeParrotData = {
        ...step1Data,
        ...step2Data,
        ...finalData
      };

      // TASK 12: Prepare knowledge base configuration for finalization
      if (sessionManager) {
        try {
          const kbEnabled = step1Data.use_knowledge_base || false;
          const finalizedKbConfig = sessionManager.prepareKnowledgeBaseForFinalization(isEditing, kbEnabled);
          
          // Add KB configuration to the finalization data
          completeParrotData.knowledge_base_config = finalizedKbConfig;
          
          console.log('TASK 12: Knowledge base configuration prepared for finalization:', {
            enabled: kbEnabled,
            filesCount: finalizedKbConfig.files?.length || 0,
            urlsCount: finalizedKbConfig.urls?.length || 0,
            mode: finalizedKbConfig.metadata?.finalization_mode
          });
        } catch (kbError) {
          console.warn('TASK 12: Failed to prepare KB configuration for finalization:', kbError);
        }
      }

      console.log('Creating/updating parrot with complete data:', completeParrotData);
      
      // Make API call to finalize the parrot configuration
      const endpoint = isEditing 
        ? `/api/parrots/${id}/wizard/finalize`
        : '/api/parrots/wizard/finalize';
        
      const response = await apiClient.post(endpoint, completeParrotData);
      
      if (isEditing) {
        showToast(`Parrot "${completeParrotData.name}" updated successfully!`, 'success');
      } else {
        showToast(`Parrot "${completeParrotData.name}" created successfully!`, 'success');
      }
      
      navigate('/parrots');
      
    } catch (error) {
      console.error('Failed to save parrot:', error);
      showToast('Failed to save parrot. Please try again.', 'error');
      setErrors({ general: 'Failed to save parrot. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle save and exit without AI chat
   */
  const handleSaveAndExit = async () => {
    try {
      setIsLoading(true);
      setErrors({});

      // TASK 12: Save parrot with current configuration (without AI chat)
      const combinedData = {
        ...step1Data,
        ...step2Data,
        system_prompt: selectedTemplate?.system_prompt || 'Default system prompt',
        first_message: step1Data.first_message || selectedTemplate?.first_message || 'Hello! How can I help you today?'
      };

      // TASK 12: Prepare knowledge base configuration for finalization
      if (sessionManager) {
        try {
          const kbEnabled = step1Data.use_knowledge_base || false;
          const finalizedKbConfig = sessionManager.prepareKnowledgeBaseForFinalization(isEditing, kbEnabled);
          
          // Add KB configuration to the finalization data
          combinedData.knowledge_base_config = finalizedKbConfig;
          
          console.log('TASK 12: Knowledge base configuration prepared for save and exit:', {
            enabled: kbEnabled,
            filesCount: finalizedKbConfig.files?.length || 0,
            urlsCount: finalizedKbConfig.urls?.length || 0,
            mode: finalizedKbConfig.metadata?.finalization_mode
          });
        } catch (kbError) {
          console.warn('TASK 12: Failed to prepare KB configuration for save and exit:', kbError);
        }
      }

      console.log('Saving parrot without AI chat:', combinedData);
      
      // Make API call to finalize the parrot configuration
      const endpoint = isEditing 
        ? `/api/parrots/${id}/wizard/finalize`
        : '/api/parrots/wizard/finalize';
        
      const response = await apiClient.post(endpoint, combinedData);
      
      if (isEditing) {
        showToast(`Parrot "${combinedData.name}" updated successfully!`, 'success');
      } else {
        showToast(`Parrot "${combinedData.name}" created successfully!`, 'success');
      }
      
      navigate('/parrots');
      
    } catch (error) {
      console.error('Failed to save parrot:', error);
      showToast('Failed to save parrot. Please try again.', 'error');
      setErrors({ general: 'Failed to save parrot. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle going back to Step 2
   */
  const handleBack = () => {
    // Use wizard navigation to handle unsaved changes
    wizardNavigation.navigateToStep(2);
  };

  return (
    <>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
      <SignedIn>
        <div className="max-w-4xl mx-auto">
          {/* Navigation Breadcrumbs */}
          <WizardBreadcrumbs 
            currentStep={3} 
            isEditing={isEditing} 
            parrotId={id}
            className="mb-6" 
          />

          {/* Progress Indicator */}
          <WizardProgressIndicator currentStep={3} />

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {isEditing ? 'Edit Parrot - Step 3' : 'Create New Parrot - Step 3'}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
              {isEditing 
                ? 'Refine your parrot\'s behavior through AI conversation'
                : 'Customize your parrot\'s behavior through AI conversation'
              }
            </p>
          </div>

          {/* Configuration Summary */}
          {formData.name && (
            <div className="mb-8 bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Configuration Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Name:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">{formData.name}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Language:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                    {formData.language === 'en' ? 'English' : 
                     formData.language === 'es' ? 'Spanish' : 
                     formData.language === 'fr' ? 'French' : 
                     formData.language === 'de' ? 'German' : 
                     formData.language === 'it' ? 'Italian' : 
                     formData.language === 'pt' ? 'Portuguese' : 
                     formData.language || 'English'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Voice:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                    {(() => {
                      const voice = voices.find(v => v.service_voice_id === formData.voice_id);
                      return voice ? voice.parrotmate_name : (formData.voice_name || formData.voice_id || 'Default');
                    })()}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">AI Behavior:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                    {formData.creativity_level === 'deterministic' ? 'Deterministic' :
                     formData.creativity_level === 'creative' ? 'Creative' :
                     formData.creativity_level === 'more_creative' ? 'More Creative' :
                     formData.creativity_level || 'Creative'}
                  </span>
                </div>
                {selectedTemplate && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Template:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">{selectedTemplate.name}</span>
                  </div>
                )}
                
                {/* Voice Quality Settings */}
                {(formData.stability || formData.similarity_boost || formData.style) && (
                  <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 dark:border-gray-700">
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2 dark:text-gray-600">Voice Quality Settings:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      {formData.stability && (
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Stability:</span>
                          <span className="ml-2 text-gray-500 dark:text-gray-500">
                            {formData.stability === 'low' ? 'Low' :
                             formData.stability === 'medium' ? 'Medium' :
                             formData.stability === 'high' ? 'High' :
                             formData.stability}
                          </span>
                        </div>
                      )}
                      {formData.similarity_boost && (
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Similarity Boost:</span>
                          <span className="ml-2 text-gray-500 dark:text-gray-500">
                            {formData.similarity_boost === 'low' ? 'Low' :
                             formData.similarity_boost === 'medium' ? 'Medium' :
                             formData.similarity_boost === 'high' ? 'High' :
                             formData.similarity_boost}
                          </span>
                        </div>
                      )}
                      {formData.style && (
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Style:</span>
                          <span className="ml-2 text-gray-500 dark:text-gray-500">
                            {formData.style === 'low' ? 'Low' :
                             formData.style === 'medium' ? 'Medium' :
                             formData.style === 'high' ? 'High' :
                             formData.style}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {errors.general && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
              <p className="font-medium">Error</p>
              <p className="text-sm">{errors.general}</p>
            </div>
          )}

          {/* Step 3 Component */}
          {formData.name && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <Step3AIConfig
                formData={formData}
                selectedTemplate={selectedTemplate}
                onComplete={handleComplete}
                onBack={handleBack}
                onSaveAndExit={handleSaveAndExit}
                errors={errors}
                isLoading={isLoading}
                isEditing={isEditing}
                existingParrotId={id}
              />
            </div>
          )}
        </div>

        {/* Navigation Confirmation Dialog */}
        <WizardNavigationConfirmDialog
          isOpen={wizardNavigation.showConfirmDialog}
          onConfirm={wizardNavigation.handleContinueWithoutSaving}
          onCancel={wizardNavigation.handleCancelNavigation}
          onSaveAndContinue={wizardNavigation.handleSaveAndContinue}
          targetStep={wizardNavigation.navigationIntent?.targetStep}
          targetStepName={wizardNavigation.navigationIntent?.targetStepName}
          changesSummary={wizardNavigation.getChangesSummary()}
          isLoading={wizardNavigation.isLoading}
        />
      </SignedIn>
    </>
  );
}
